import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import {
    ArrowUpRight,
    Clock,
    MapPin,
    Phone
} from 'lucide-react'
import { LogoMark } from '@/components/generals/logo'
import { buttonClasses } from '@/components/generals/button'
import { SectionHeading } from '@/components/landing/section-heading'
import { getHoursSummary } from '@/lib/hours'
import { directionsUrl, siteConfig } from '@/constants/site'

const hours = getHoursSummary()

const iconClasses = 'inline-flex size-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary'

export const VisitSection: FC = (): ReactNode => {
    return (
        <section id='visit' aria-labelledby='visit-title' className='scroll-mt-20 py-20 sm:py-28'>
            <div className='mx-auto grid max-w-6xl gap-5 px-5 sm:px-8 lg:grid-cols-[1.15fr_0.85fr]'>
                <div className='rounded-[2rem] border border-border bg-card p-7 shadow-sm shadow-broth/5 sm:p-10'>
                    <SectionHeading
                        titleId='visit-title'
                        eyebrow="Visit us"
                        title={<>Come in from <em>the cold.</em></>}
                        description="A small counter, a big pot and a few seats by the window. Stay for a bowl or take it to go."
                    />

                    <dl className='mt-10 grid gap-8 sm:grid-cols-2'>
                        <div className='flex gap-4'>
                            <span className={iconClasses}>
                                <MapPin className='size-5' aria-hidden='true' />
                            </span>

                            <div>
                                <dt className='text-sm font-semibold'>
                                    Address
                                </dt>

                                <dd className='mt-1 text-muted-foreground'>
                                    <address className='not-italic leading-relaxed'>
                                        {siteConfig.address.street}
                                        <br />
                                        {siteConfig.address.city}, {siteConfig.address.region} {siteConfig.address.postalCode}
                                    </address>

                                    <a
                                        href={directionsUrl}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='mt-2 inline-flex items-center gap-1 rounded-sm font-semibold text-primary underline-offset-4 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring'
                                    >
                                        Get directions
                                        <ArrowUpRight className='size-4' aria-hidden='true' />
                                    </a>
                                </dd>
                            </div>
                        </div>

                        <div className='flex gap-4'>
                            <span className={iconClasses}>
                                <Clock className='size-5' aria-hidden='true' />
                            </span>

                            <div>
                                <dt className='text-sm font-semibold'>
                                    Hours
                                </dt>

                                <dd className='mt-1 space-y-2'>
                                    {hours.map(group => (
                                        <p key={group.label} className='leading-snug'>
                                            <span className='block text-muted-foreground'>
                                                {group.label}
                                            </span>

                                            <span className='block font-medium'>
                                                {group.hours}
                                            </span>
                                        </p>
                                    ))}
                                </dd>
                            </div>
                        </div>

                        <div className='flex gap-4'>
                            <span className={iconClasses}>
                                <Phone className='size-5' aria-hidden='true' />
                            </span>

                            <div>
                                <dt className='text-sm font-semibold'>
                                    Phone
                                </dt>

                                <dd className='mt-1'>
                                    <a
                                        href={siteConfig.phoneHref}
                                        className='rounded-sm font-medium underline decoration-foreground/25 underline-offset-4 outline-none transition-colors hover:decoration-foreground focus-visible:ring-2 focus-visible:ring-ring'
                                    >
                                        {siteConfig.phone}
                                    </a>
                                </dd>
                            </div>
                        </div>
                    </dl>
                </div>

                <div className='relative isolate flex flex-col overflow-hidden rounded-[2rem] bg-scallion p-7 text-white shadow-xl shadow-scallion/20 sm:p-10'>
                    <LogoMark className='absolute -bottom-10 -right-10 -z-10 size-64 text-white opacity-[0.12] [&_.fill-primary]:fill-white' />

                    <p className='inline-flex items-center gap-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-white/85'>
                        <span aria-hidden='true' className='h-px w-6 bg-white/60' />
                        Feeding a crowd
                    </p>

                    <h3 className='mt-4 text-3xl font-semibold leading-tight sm:text-4xl'>
                        Big pots for <em className='font-normal italic'>big days.</em>
                    </h3>

                    <p className='mt-4 leading-relaxed text-white/85'>
                        Our Family Pot serves four, and we cook larger batches for offices, birthdays and get-well meal trains. Give us a day&apos;s notice for orders of more than ten bowls.
                    </p>

                    <div className='mt-auto flex flex-col gap-3 pt-10 sm:flex-row lg:flex-col xl:flex-row'>
                        <a href={siteConfig.phoneHref} className={buttonClasses('light', 'md')}>
                            <Phone />
                            Call to plan an order
                        </a>

                        <Link href='/#order' className={buttonClasses('outline-light', 'md', 'border-white/40 text-white hover:border-white hover:bg-white/10')}>
                            Order a Family Pot
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    )
}
